import type { TFunction } from 'i18next';
import type { FilterItem, FilterOption, FilterCategoryItem, ActiveFilterPill } from './types';

/**
 * Translation key conventions for the screening filter catalog. Keys are
 * derived from the filter's `id` so adding a new filter only requires adding
 * one stanza per locale to translations/<locale>.json. Missing keys fall back
 * to the English literal stored on the filter object (acts as the canonical
 * source until a translator adds locale strings).
 *
 * Convention:
 *   filters.config.<filter_id>.label
 *   filters.config.<filter_id>.short
 *   filters.config.<filter_id>.description
 *   filters.config.<filter_id>.options.<option_value>.label
 *   filters.config.<filter_id>.options.<option_value>.subtitle
 *   filters.categories.<category_id>.label
 *
 * `tFilter(filter, t)` returns translated `{ label, shortDescription, description }`.
 * `tOption(filter, option, t)` returns translated `{ label, subtitle }`.
 * `tCategory(category, t)` returns translated `label`.
 */

// Sanitise option value into a JSON-safe i18n sub-key.
const optionKey = (raw: unknown): string =>
  String(raw)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '') || 'unknown';

// Helper: only use a translated string when it's non-empty; otherwise fall
// back to the literal. Guards against an accidental "" entry in en.json
// silently blanking a label.
const trOrFallback = (
  t: TFunction,
  key: string,
  fallback: string | undefined,
): string | undefined => {
  if (fallback === undefined) return undefined;
  const result = t(key, { defaultValue: fallback });
  return result === '' ? fallback : result;
};

export interface TranslatedFilterText {
  label?: string;
  shortDescription?: string;
  description?: string;
}

export const tFilter = (filter: FilterItem, t: TFunction): TranslatedFilterText => {
  const baseKey = `filters.config.${filter.id}`;
  const out: TranslatedFilterText = {};
  const label = trOrFallback(t, `${baseKey}.label`, filter.label);
  const short = trOrFallback(t, `${baseKey}.short`, filter.shortDescription);
  const desc = trOrFallback(t, `${baseKey}.description`, filter.description);
  if (label !== undefined) out.label = label;
  if (short !== undefined) out.shortDescription = short;
  if (desc !== undefined) out.description = desc;
  return out;
};

export interface TranslatedOption {
  value: string;
  label: string;
  subtitle?: string;
  icon?: FilterOption['icon'];
  badge?: FilterOption['badge'];
}

export const tOption = (
  filter: FilterItem,
  option: FilterOption,
  t: TFunction,
): TranslatedOption => {
  const baseKey = `filters.config.${filter.id}.options.${optionKey(option.value)}`;
  const result: TranslatedOption = {
    value: option.value,
    label: trOrFallback(t, `${baseKey}.label`, option.label) ?? option.label,
  };
  const subtitle = trOrFallback(t, `${baseKey}.subtitle`, option.subtitle);
  if (subtitle !== undefined) result.subtitle = subtitle;
  if (option.icon !== undefined) result.icon = option.icon;
  if (option.badge !== undefined) result.badge = option.badge;
  return result;
};

/** Returns a shallow-translated copy of a FilterItem so callers can pass
 *  filter.options / label / etc. straight to existing controls without
 *  threading a `t` function through the component tree. */
export const translateFilter = (filter: FilterItem, t: TFunction): FilterItem => {
  const text = tFilter(filter, t);
  const next: FilterItem = { ...filter, ...text };
  if (filter.options) {
    next.options = filter.options.map((opt) => {
      const translated = tOption(filter, opt, t);
      const result: FilterOption = {
        value: opt.value,
        label: translated.label,
      };
      if (translated.subtitle !== undefined) result.subtitle = translated.subtitle;
      if (translated.icon !== undefined) result.icon = translated.icon;
      if (translated.badge !== undefined) result.badge = translated.badge;
      return result;
    });
  }
  if (filter.children) {
    next.children = filter.children.map((child) => translateFilter(child, t));
  }
  return next;
};

export const tCategory = (
  category: FilterCategoryItem,
  t: TFunction,
): FilterCategoryItem => ({
  ...category,
  label:
    trOrFallback(
      t,
      `filters.categories.${category.id}.label`,
      category.label,
    ) ?? category.label,
});

export const tCategories = (
  categories: FilterCategoryItem[],
  t: TFunction,
): FilterCategoryItem[] => categories.map((c) => tCategory(c, t));

/** Translate the labels of pre-built ActiveFilterPill objects produced by
 *  useFilterState. Uses the same `filters.config.<id>.label` key convention
 *  as the rest of the catalog, so a single locale entry covers both the
 *  form-field label and its corresponding active-filter chip. */
export const tPills = (
  pills: ActiveFilterPill[],
  t: TFunction,
): ActiveFilterPill[] =>
  pills.map((p) => ({
    ...p,
    label:
      trOrFallback(t, `filters.config.${p.id}.label`, p.label) ?? p.label,
  }));
